import { useCallback, useState } from "react";
import { NavLink, Outlet } from "react-router-dom";
import { BookOpen, LayoutDashboard, LogOut, Menu, UserPlus, Users, X, Zap } from "lucide-react";
import { useAuth } from "../auth/AuthProvider";

interface NavDef {
  to: string;
  label: string;
  icon: JSX.Element;
}

const ANALYTICS: NavDef[] = [
  {
    to: "/overview",
    label: "Vue d'ensemble",
    icon: <LayoutDashboard size={16} />,
  },
];

const TENANTS: NavDef[] = [
  {
    to: "/tenants",
    label: "Tous les tenants",
    icon: <Users size={16} />,
  },
  {
    to: "/create",
    label: "Créer un tenant",
    icon: <UserPlus size={16} />,
  },
];

const CONTENT: NavDef[] = [
  {
    to: "/kb",
    label: "Base de connaissances",
    icon: <BookOpen size={16} />,
  },
];

function NavSection({ title, items, onNavigate }: { title: string; items: NavDef[]; onNavigate?: () => void }) {
  return (
    <>
      <div className="nav-section">{title}</div>
      {items.map((item) => (
        <NavLink
          key={item.to}
          to={item.to}
          onClick={onNavigate}
          className={({ isActive }) => "nav-item" + (isActive ? " is-active" : "")}
        >
          {item.icon}
          {item.label}
        </NavLink>
      ))}
    </>
  );
}

export function AppShell() {
  const { logout } = useAuth();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const closeSidebar = useCallback(() => setSidebarOpen(false), []);

  return (
    <div className="app is-visible" id="app-shell">
      <header className="mobile-topbar">
        <button type="button" className="mobile-menu-btn" onClick={() => setSidebarOpen(true)} aria-label="Menu">
          <Menu size={20} />
        </button>
        <div className="mobile-brand">
          <div className="sidebar-mark"><Zap size={14} /></div>
          <span className="sidebar-name">Platform</span>
        </div>
      </header>

      {sidebarOpen && <div className="sidebar-backdrop" onClick={closeSidebar} />}

      <aside className={"sidebar" + (sidebarOpen ? " is-open" : "")}>
        <div className="sidebar-brand">
          <div className="sidebar-mark"><Zap size={14} /></div>
          <div>
            <div className="sidebar-name">Platform</div>
            <div className="sidebar-sub">Super Admin</div>
          </div>
          <button type="button" className="sidebar-close-btn" onClick={closeSidebar} aria-label="Fermer">
            <X size={16} />
          </button>
        </div>
        <nav className="sidebar-nav">
          <NavSection title="Analytics" items={ANALYTICS} onNavigate={closeSidebar} />
          <div style={{ height: 8 }} />
          <NavSection title="Tenants" items={TENANTS} onNavigate={closeSidebar} />
          <div style={{ height: 8 }} />
          <NavSection title="Contenu" items={CONTENT} onNavigate={closeSidebar} />
        </nav>
        <div className="sidebar-footer">
          <button type="button" className="nav-item" onClick={logout}>
            <LogOut size={16} />
            Déconnexion
          </button>
        </div>
        <div className="sidebar-powered">
          Powered by<br />
          <a href="https://amsolutions.tech" target="_blank" rel="noopener noreferrer">
            AM Solutions
          </a>
        </div>
      </aside>
      <main className="main">
        <Outlet />
      </main>
    </div>
  );
}
